import useSWR from 'swr';

import { fetchTicketsFilter } from '~/data/hooks/fetchTicketsFilter';
import type {
  API_TicketFilterSearchParams,
  Ticket,
  TicketWithPortfolio,
} from '~/data/schemas/ticket';

export const mapTicketToTicketWithPortfolio = (ticket: Ticket): TicketWithPortfolio => {
  return {
    ...ticket,
    // Flat the portfolio to be used by the table. `property` is null when the
    // ticket's Property was deleted (a SET_NULL FK), and its portfolio is
    // nullable too, so carry null through rather than assume (AMP-585/AMP-593).
    portfolio: ticket.property?.portfolio ?? null,
  } as TicketWithPortfolio;
};

const ticketsFetcher = async ([_, params]: readonly ['tickets', API_TicketFilterSearchParams]) => {
  return await fetchTicketsFilter(params);
};

export type UseTicketsOptions = API_TicketFilterSearchParams;

/**
 * @param enabled pass `false` to hold the request — same escape hatch `useInvoices`
 * has, for callers that only want the list once something is on screen.
 */
export function useTickets(params: UseTicketsOptions, enabled: boolean = true) {
  const { data, isLoading } = useSWR(
    enabled && params ? (['tickets', params] as const) : null,
    ticketsFetcher,
    // This is needed to prevent MUI datagrid from resetting the pagination model when the data changes
    // This is caused because the numRows resets to 0 when the data changes
    // See https://mui.com/x/react-data-grid/pagination/#index-based-pagination
    { keepPreviousData: true },
  );

  const tickets = data?.results?.map((result) => mapTicketToTicketWithPortfolio(result)) ?? [];
  const numTickets = data?.metadata.numTickets ?? 0;
  const metadata = data?.metadata ?? null;

  return {
    tickets,
    numTickets,
    metadata,
    isLoading,
  };
}
